import { TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { Damage, Quantum } from "../../shared/CaseModel";
import { damageName } from "../../shared/damages";
import { YesNoDialog } from "../general/YesNoDialog";
import { ClaimTableHead } from "./ClaimTableHead";
import { Party } from "./IssueModel";
import { useJudgment } from "./JudgedRow";
import { QuantumRow, QuantumRowCallbacks } from "./QuantumRow";

export interface QuantumEditorRowsProps {
  quantums: Quantum[];
  // the damages the amounts claim, which name them
  damages: Damage[];
  defendants: Party[];
  callbacks: QuantumTableCallbacks;
  claim: { id: string; title: string; ordinal: string };
  firstColSpan?: number;
}

export const QuantumEditorRows = ({
  quantums,
  damages,
  defendants,
  callbacks,
  claim,
  firstColSpan = 2,
}: QuantumEditorRowsProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.claims" });
  const [quantumToDelete, setQuantumToDelete] = useState<Quantum | undefined>();

  const summaryColumn = defendants.length > 1;
  const { currency } = useCurrency();
  const interest = useJudgment()?.interest?.find(
    ({ issueId }) => issueId === claim.id,
  );

  const quantumRowCallbacks: QuantumRowCallbacks = {
    onQuantumEditorRequested: callbacks.onQuantumEditorRequested,
    onLiabilityUpdate: (quantum, defendantId, newLiability) => {
      const newQuantum: Quantum = JSON.parse(JSON.stringify(quantum));
      if (newQuantum.liabilityShares === undefined)
        newQuantum.liabilityShares = {};
      newQuantum.liabilityShares[defendantId] = newLiability;
      callbacks.onQuantumUpdate(newQuantum);
    },
    onQuantumDelete: setQuantumToDelete,
  };
  return (
    <>
      <YesNoDialog
        onYes={() => {
          if (quantumToDelete !== undefined)
            callbacks.onQuantumDelete(quantumToDelete);
          setQuantumToDelete(undefined);
        }}
        yesColor="error"
        onNo={() => setQuantumToDelete(undefined)}
        open={quantumToDelete !== undefined}
        title={t("issues.quantums.deleteDialog.title")}
        description={t("issues.quantums.deleteDialog.description", {
          name:
            quantumToDelete && damageName(damages, quantumToDelete.damageId),
          claimTitle: claim.title,
        })}
      />
      <ClaimTableHead
        label={t("quantums.amountTitle")}
        firstColSpan={firstColSpan}
        markers={{ label: "amounts", defendants: "liability" }}
        // who has settled is said once, over the win chances above
        defendants={defendants.map(({ id, name }) => ({ id, name }))}
        summary={
          summaryColumn
            ? {
                group: t("quantums.liability.total"),
                first: t("quantums.liability.most"),
                second: t("quantums.liability.least"),
              }
            : undefined
        }
      />
      <TableBody>
        {quantums?.map((quantum) => (
          <QuantumRow
            key={"quantum_" + quantum.damageId}
            claimId={claim.id}
            issueTitle={claim.title}
            issueOrdinal={claim.ordinal}
            quantum={quantum}
            name={damageName(damages, quantum.damageId)}
            defendants={defendants}
            callbacks={quantumRowCallbacks}
            indent={firstColSpan === 1}
            summaryColumn={summaryColumn}
          />
        ))}
        {interest && (
          <TableRow>
            <TableCell colSpan={firstColSpan} sx={{ borderBottom: "none" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("quantums.interestInJudgment", {
                  amount: currency.format(interest.amount),
                })}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </>
  );
};

export interface QuantumTableCallbacks {
  onQuantumEditorRequested: (quantum?: Quantum) => void;
  onQuantumDelete: (quantum: Quantum) => void;
  onQuantumUpdate: (quantum: Quantum) => void;
}
